import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserAuthData, getFullName } from './authData.model';
import { outlineService } from './outline.service';
import { env, python, envPython } from 'src/environments/environments';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})

export class AuthService {

  private readonly fastapi_url_base = `${envPython.url_base}`;
  private currentUserSubject = new BehaviorSubject<UserAuthData | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private outlineService: outlineService,
    private snackBar: MatSnackBar
  ) {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
  }

  //Método de login reestructurado
  loginWeb(
    email: string,
    password: string
  ): Observable<{ success: boolean; message: string; data?: UserAuthData; status?: number }> {
    const encryptedPassword = CryptoJS.MD5(password).toString();
    const body = { email, password: encryptedPassword };

    return this.http.post<{ success: boolean; message: string; data?: any; status?: number }>(
      `${this.fastapi_url_base}/login`,
      body,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .pipe(
      map((response) => {
        if (response.success && response.data) {
          const user: UserAuthData = this.mapUserData(response.data);

          // Verifica si el perfil del usuario cambió
          const previousUser = localStorage.getItem('user');
          if (previousUser && JSON.parse(previousUser).idPerfil !== user.idPerfil) {
            this.clearCache(); // Limpiar la cache si el perfil cambió
            this.loadPermissionsAndNavigate(user); // Cargar permisos y navegar a la página principal
          } else {
            this.saveUserData(user); // Guardar los datos del usuario si no hubo cambio
            this.showSnackBar('Inicio de sesión exitoso', 'success');
          }
          return { success: true, message: 'Login successful', data: user };
          
        } else if (response.success && response.status === 2) {
          return { success: true, message: 'User inactive', status: 2 };

        } else if (response.success && response.status === 3) {
          return { success: true, message: 'User blocked', status: 3 };

        } else {
          this.showSnackBar('Credenciales inválidas', 'error');
          return { success: false, message: 'Invalid credentials' };
        }
      }),
      catchError((error) => {
        console.error('Login error:', error);
        this.showSnackBar('Error en el servidor: ' + error.message, 'error');
        return of({ success: false, message: error.message });
      })
    );
  }

  // Método para cargar permisos y redirigir a la página de inicio
  private loadPermissionsAndNavigate(user: UserAuthData): void {
    this.outlineService.cargarPermisos(user.idPerfil).subscribe({
      next: () => {
        this.saveUserData(user); // Guardar los datos del nuevo usuario
        this.router.navigate(['/home']);
      },
      error: () => {
        this.showSnackBar('Error al cargar los permisos. Por favor, intente nuevamente.', 'error');
      }
    });
  }

  private mapUserData(data: any): UserAuthData {
    return {
      idUser: data.idUsuario,
      idEmp: data.idEmp,
      idPerfil: data.idperfil,
      perfil: data.perfil,
      last_session: data.last_session,
      nombres: data.nombres,
      apellidos: data.apellidos,
      tipo_doc: data.tipo_doc,
      documento: data.documento,
      id_cargo: data.id_cargo,
      cargo: data.cargo,
      correo_corporativo: data.correo_corporativo,
      correo_alterno: data.correo_alterno,
      telefono: data.telefono,
      direccion: data.direccion,
      imagen: data.imagen,
      status: data.status,
      pass: data.pass,
      passtemp: data.passtemp,
      nomstatus: data.nomstatus,
    };
  }

  private saveUserData(user: UserAuthData): void {
    this.currentUserSubject.next(user);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('loginTime', JSON.stringify({ loginTime: new Date().getTime() }));
  }

  private clearCache(): void {
    // Limpia datos de la cache relacionados con el usuario
    localStorage.removeItem('userPermissions');
    // Puedes agregar más elementos a limpiar si es necesario
  }

  private showSnackBar(message: string, type: 'success' | 'error'): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      panelClass: type === 'success' ? 'snack-bar-success' : 'snack-bar-error',
    });
  }

  logout(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('token');
    this.currentUserSubject.next(null);
    this.router.navigate(['/inicio-sesion']);
  }

  isLoggedIn(): boolean {
    const loginTime = localStorage.getItem('loginTime');
    const loginData = loginTime ? JSON.parse(loginTime) : null;
    if (loginData) {
      const currentTime = new Date().getTime();
      const timeDiffMinutes = (currentTime - loginData.loginTime) / 60000;
      if (timeDiffMinutes > 60) {
        this.logout();
        return false;
      }
      return !!localStorage.getItem('user');
    }
    return false;
  }

  getCurrentUser(): UserAuthData | null {
    return this.currentUserSubject.value;
  }

  getCurrentUserFullName(): string {
    const user = this.getCurrentUser();
    return user ? getFullName(user) : '';
  }

  updateUser(user: UserAuthData) {
    this.currentUserSubject.next(user);
    localStorage.setItem('user', JSON.stringify(user));
  }

  updateUserImage(imageUrl: string): void {
    let currentUser = this.currentUserSubject.value;
    if (currentUser) {
      currentUser.imagen = imageUrl;
      this.currentUserSubject.next(currentUser);
    }
  }

  hasRole(role: string): boolean {
    const currentUser = this.getCurrentUser();
    return currentUser ? currentUser.perfil === role : false;
  }

  updateProfileData(partialUserData: Partial<UserAuthData>) {
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...partialUserData };
      this.currentUserSubject.next(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }
}
